import { useState, useCallback, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import PilotHeader from "./components/PilotHeader";
import PilotSidebarPanel, { type PilotLayer } from "./components/PilotSidebarPanel";
import PilotMapPanel from "./components/PilotMapPanel";
import PilotIndicators from "./components/PilotIndicators";
import PilotBlocksTable from "./components/PilotBlocksTable";
import PilotTreeList from "./components/PilotTreeList";
import NoRealData from "@/components/shared/NoRealData";
import { api } from "@/lib/api";
import { useAreasPiloto, useInspectionsWithQuadras, useQuadrasList } from "@/hooks";
import { toTreeMapPoint, type QuadraComAnalise, type TreeMapPoint } from "@/types/cta-mapa-ui";
import { riscoLabel, SBV_AREA_PILOTO_CODE } from "@/config/cta-mapa-config";
import type { InspectionQuadraLink } from "@/types/territorial";

const LAYERS: PilotLayer[] = [
  { id: "perimetro", label: "Perímetro da área piloto", cor: "#1B6E4F" },
  { id: "quadras", label: "Quadras", cor: "#2563eb" },
  { id: "fita", label: "Ilha de calor por quadra (FITA, preliminar)", cor: "#f97316" },
  { id: "arvores", label: "Exemplares cadastrados", cor: "#16a34a" },
];

export default function PilotoSBVPage() {
  const { areasPiloto, loading: areasLoading, error: areasError } = useAreasPiloto();
  const area = useMemo(() => areasPiloto.find((a) => a.code === SBV_AREA_PILOTO_CODE) ?? null, [areasPiloto]);

  const { quadras: quadrasRaw, loading: quadrasLoading } = useQuadrasList(area?.id ?? null);
  const quadras = useMemo(() => (area ? quadrasRaw.filter((q) => q.areaPilotoId === area.id) : []), [quadrasRaw, area]);

  const { links } = useInspectionsWithQuadras();

  // Exemplares do projeto vinculado a area piloto.
  const [trees, setTrees] = useState<TreeMapPoint[]>([]);
  const [treesLoaded, setTreesLoaded] = useState(false);
  useEffect(() => {
    if (!area) return;
    if (area.projectId == null) {
      setTreesLoaded(true);
      return;
    }
    let active = true;
    api
      .trees(area.projectId)
      .then((rows) => {
        if (active) setTrees(rows.map(toTreeMapPoint).filter((t): t is TreeMapPoint => t !== null));
      })
      .catch(() => {
        if (active) setTrees([]);
      })
      .finally(() => {
        if (active) setTreesLoaded(true);
      });
    return () => {
      active = false;
    };
  }, [area]);

  // Inspecao territorial mais recente de cada quadra desta area.
  const quadrasComAnalise = useMemo<QuadraComAnalise[]>(() => {
    const latest = new Map<number, InspectionQuadraLink>();
    for (const l of links) {
      if (l.quadraId == null) continue;
      const prev = latest.get(l.quadraId);
      if (!prev || l.inspectionDate > prev.inspectionDate) latest.set(l.quadraId, l);
    }
    return quadras
      .map((q) => ({ quadra: q, analise: latest.get(q.id) ?? null }))
      .sort((a, b) => a.quadra.code.localeCompare(b.quadra.code, "pt-BR", { numeric: true }));
  }, [quadras, links]);

  const [activeLayers, setActiveLayers] = useState<Set<string>>(new Set(LAYERS.map((l) => l.id)));
  const [selectedTree, setSelectedTree] = useState<TreeMapPoint | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const handleToggleLayer = useCallback((id: string) => {
    setActiveLayers((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleSelectTree = useCallback((tree: TreeMapPoint) => {
    setSelectedTree((prev) => (prev?.id === tree.id ? null : tree));
  }, []);

  if (!area) {
    return (
      <div className="p-6">
        {areasLoading ? (
          <p className="text-sm text-foreground-500">Carregando área piloto…</p>
        ) : (
          <NoRealData
            variant="page"
            icon="ri-map-pin-line"
            title="Área piloto do Setor Bela Vista não encontrada"
            description={
              areasError
                ? `Não foi possível carregar as áreas piloto: ${areasError}`
                : `A área piloto ${SBV_AREA_PILOTO_CODE} não está cadastrada para esta organização.`
            }
          />
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-0px)] overflow-hidden bg-background-50">
      <PilotHeader area={area} quadrasMapeadas={quadras.length} />

      <div className="px-4 py-3 shrink-0">
        <PilotIndicators area={area} quadras={quadrasComAnalise} trees={trees} treesLoaded={treesLoaded} />
      </div>

      <div className="flex flex-1 overflow-hidden px-4 pb-4 gap-3">
        <div className={`shrink-0 transition-all duration-300 ${sidebarCollapsed ? "w-0 overflow-hidden" : "w-[300px]"}`}>
          <PilotSidebarPanel
            area={area}
            quadrasMapeadas={quadras.length}
            camadas={LAYERS}
            activeLayers={activeLayers}
            onToggleLayer={handleToggleLayer}
          />
        </div>

        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="shrink-0 w-5 h-full bg-white border border-background-200/70 rounded-card flex items-center justify-center hover:bg-background-100 transition-colors self-stretch"
          title={sidebarCollapsed ? "Expandir painel" : "Recolher painel"}
        >
          <i className={sidebarCollapsed ? "ri-arrow-right-s-line text-foreground-400 text-xs" : "ri-arrow-left-s-line text-foreground-400 text-xs"}></i>
        </button>

        <div className="flex-1 flex flex-col gap-3 min-w-0 overflow-hidden">
          <div className="flex-1 min-h-0">
            <PilotMapPanel
              area={area}
              quadras={quadrasComAnalise}
              trees={trees}
              activeLayers={activeLayers}
              selectedTree={selectedTree}
              onSelectTree={handleSelectTree}
            />
          </div>

          <div className="flex flex-col lg:flex-row gap-3 shrink-0 h-[280px]">
            <div className="flex-1 min-w-0 overflow-hidden">
              <PilotBlocksTable quadras={quadrasComAnalise} loading={quadrasLoading} />
            </div>
            <div className="flex-1 min-w-0 overflow-hidden">
              <PilotTreeList arvores={trees} loaded={treesLoaded} onSelectTree={handleSelectTree} />
            </div>
          </div>
        </div>
      </div>

      {selectedTree && (
        <div className="shrink-0 bg-white border-t border-background-200/70 px-4 py-2.5 flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center">
              <i className="ri-plant-line text-primary-600 text-xs"></i>
            </div>
            <div>
              <p className="text-xs font-semibold text-foreground-800">
                {selectedTree.nomePopular || "Espécie não identificada"} — {selectedTree.codigoCta}
              </p>
              <p className="text-[10px] text-foreground-500">{selectedTree.logradouro || "Sem endereço informado"}</p>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-background-100 text-foreground-700">
              {selectedTree.classeRisco ? `Risco: ${riscoLabel(selectedTree.classeRisco)}` : "Sem avaliação de risco"}
            </span>
            <Link to={`/cta/${selectedTree.id}`} className="text-[11px] font-medium text-primary-600 hover:underline">
              Ver FCA
            </Link>
            <button
              onClick={() => setSelectedTree(null)}
              className="w-6 h-6 rounded-md hover:bg-background-100 flex items-center justify-center text-foreground-400 transition-colors"
              aria-label="Fechar"
            >
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
